import { useForm } from "react-hook-form";
import { Box, Button } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { schemaCreatePassword } from "../scheme";
import { FC, useEffect, useRef, useState } from "react";
import LoadingSvg from "@/assets/svg/LoadingSvg";
import { RegisterUserRequest } from "@/store/api/authApiData";
import { styles } from "../stylesSignUp";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import HeaderControl from "./HeaderControl";
import TitleBlock from "./TitleBlock";
import BottomLink from "./BottomLink";

type FormInput = {
  password: string;
  confirmPassword: string;
};

interface CreatePasswordProps {
  setSignUpStep: React.Dispatch<React.SetStateAction<number>>;
  setSignUpData: (data: Partial<RegisterUserRequest>) => void;
  signUpData: RegisterUserRequest;
}

const CreatePassword: FC<CreatePasswordProps> = ({ setSignUpStep, setSignUpData, signUpData }) => {
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schemaCreatePassword),
    mode: "onChange",
  });

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");
  const [touchedConfirm] = useState(false);
  const [showMismatchError, setShowMismatchError] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    register("confirmPassword").onChange(e);

    setShowMismatchError(false);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      trigger("confirmPassword");
      setShowMismatchError(true);
    }, 500);
  };

  useEffect(() => {
    if (!touchedConfirm) return;

    const delayDebounce = setTimeout(() => {
      trigger("confirmPassword");
    }, 10500);

    return () => clearTimeout(delayDebounce);
  }, [confirmPasswordValue, passwordValue, touchedConfirm]);

  useEffect(() => {
    trigger("confirmPassword");
  }, [watch("password")]);

  const onSubmit = async (data: FormInput, e: any) => {
    e.preventDefault();
    // setLoading(true);

    await new Promise((resolve) => setTimeout(resolve, 2000));
    setSignUpData({
      ...signUpData,
      password: data.password,
      passwordconf: data.confirmPassword,
    });

    setLoading(false);
    setSignUpStep(3);
  };

  const [loading, setLoading] = useState(false);

  return (
    <Box className={loading ? "blurred" : ""}>
      <Box
        sx={[
          styles.wrapper,
          {
            ...(loading && {
              filter: "blur(4px)",
              pointerEvents: "none",
              userSelect: "none",
            }),
          },
        ]}
      >
        <HeaderControl onClick={() => setSignUpStep(1)} logo />
        <Box sx={{ marginTop: "32px" }}>
          <TitleBlock title={"Sign up"} subtitle="Create your passwod" />

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              width: "100%",
              marginTop: "44px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              <CustomInput
                label="Password"
                placeholder="Enter your password"
                type="password"
                error={!!errors.password}
                helperText={errors.password?.message || null}
                register={register}
                name="password"
              />

              <CustomInput
                label="Confirm Password"
                placeholder="Confirm Password"
                type="password"
                error={!!errors.confirmPassword && showMismatchError}
                helperText={
                  showMismatchError && errors.confirmPassword?.message
                    ? errors.confirmPassword.message
                    : null
                }
                register={register}
                name="confirmPassword"
                onChange={handleConfirmChange}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              // disabled={
              //   !passwordValue ||
              //   !confirmPasswordValue ||
              //   passwordValue !== confirmPasswordValue ||
              //   !!errors.password ||
              //   !!errors.confirmPassword
              // }
              sx={[
                styles.continueButton,
                {
                  marginTop: "2.625rem",
                },
              ]}
            >
              Continue
            </Button>
            <BottomLink sx={{ mt: 4 }} />
          </Box>
        </Box>
      </Box>
      {loading && (
        <Box className={loading ? "blurred" : ""} sx={{ position: "relative", minHeight: "100vh" }}>
          <LoadingSvg
            width={42}
            height={42}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CreatePassword;
